/* eslint-disable camelcase */
import { convertToCents } from '../helpers/format-money';
import { createElement, error } from 'client-portal/utils/banners';

export const appearance = {
  variables: {
    borderRadius: '2px',
    colorDanger: '#ea3917',
    colorText: '#212121',
    fontFamily: 'Inter, Helvetica, Arial, sans-serif',
    fontLineHeight: '1.2em',
    fontSizeBase: '16px',
    gridColumnSpacing: '30px',
    gridRowSpacing: '23px',
  },
  rules: {
    '.Input': {
      padding: '16px 12px',
      border: '1px solid #ddd',
      outline: 'none',
    },
    '.Input:focus': {
      borderColor: '#339967',
      boxShadow: '0 0 0 3px #e6f2ec',
    },
    '.Input:hover': {
      borderColor: '#339967',
    },
    '.Input--invalid, .Input--invalid:hover, .Input--invalid:focus': {
      border: '1px solid #ea3917',
      boxShadow: 'none',
    },
    '.Label': {
      fontWeight: '600',
      marginBottom: '8px',
      color: '#333',
      fontSize: '14px',
    },
    '.Error': {
      marginTop: '8px',
    },
  },
};

function _applyLinkPassthroughMode(options, opts, paymentMethodType) {
  if (opts.featureStripeLinkPassthroughMode) {
    if (paymentMethodType !== 'us_bank_account') delete options.paymentMethodTypes;
    options.currency = opts.currency?.toLowerCase() || 'usd';
    options.onBehalfOf = opts.onBehalfOf;
  }
}

export function buildStripeSetupModeElement(stripe, opts = {}) {
  let paymentMethodType = opts.paymentMethodType || 'card';
  let options = {
    mode: 'setup',
    paymentMethodTypes: [paymentMethodType],
    appearance,
  };

  if (paymentMethodType === 'us_bank_account') {
    options.paymentMethodOptions = {
      us_bank_account: {
        verification_method: 'instant',
      },
    };
  }

  _applyLinkPassthroughMode(options, opts, paymentMethodType);

  if (opts.locale) options.locale = opts.locale;

  return stripe.elements(options);
}

export function buildStripePaymentModeElement(stripe, opts = {}) {
  let paymentOptions = {
    mode: 'payment',
    currency: opts.currency?.toLowerCase() || 'usd',
    onBehalfOf: opts.onBehalfOf,
    paymentMethodTypes: opts.paymentMethodTypes || ['card'],
    paymentMethodCreation: 'manual',
    amount: convertToCents(opts.amount),
    appearance,
  };

  if (opts.featureStripeLinkPassthroughMode) {
    delete paymentOptions.paymentMethodTypes;
  }

  if (opts.locale) {
    paymentOptions.locale = opts.locale;
  }

  return stripe.elements(paymentOptions);
}

function _buildBillingDetails({ email, phone }) {
  if (!email && !phone) return null;
  return {
    ...(email && { email }),
    ...(phone && { phone: `${phone}` }),
  };
}

export function buildStripePaymentElement(elements, opts = {}) {
  let wallets = { googlePay: 'never', applePay: 'never' };

  if (!opts.featureStripeLinkPassthroughMode || opts.disableLink) {
    wallets.link = 'never';
  }

  let options = {
    terms: { card: 'never' },
    wallets,
  };

  let billingDetails = _buildBillingDetails(opts);
  if (billingDetails) {
    options.defaultValues = { billingDetails };
  }

  let paymentElement = elements.create('payment', options);
  paymentElement.on('ready', opts.onReady || (() => {}));
  return paymentElement;
}

export function buildStripeExpressCheckoutElement(elements, callBackOptions = {}) {
  let expressCheckoutOptions = {
    wallets: {
      googlePay: 'never',
      applePay: 'never',
    },
  };
  let events = [
    ['ready', 'onReady'],
    ['loaderror', 'onLoadError'],
    ['confirm', 'onConfirm'],
    ['click', 'onClick'],
    ['cancel', 'onCancel'],
  ];

  let expressCheckoutElement = elements.create('expressCheckout', expressCheckoutOptions);

  events.forEach(([event, callback]) => {
    expressCheckoutElement.on(event, callBackOptions[callback] || (() => {}));
  });

  return expressCheckoutElement;
}

export async function confirmSetupIntent(setupIntent, { elements, stripe }, options = {}) {
  let errorHandler = options.errorHandler || _errorBanner;

  if (setupIntent.isTest) {
    return _buildTestPaymentMethod(setupIntent.stripeCustomerId, setupIntent.paymentMethodTypes);
  }

  try {
    let setupIntentConfirmation = await _confirmSetupIntent(
      setupIntent,
      { elements, stripe },
      options
    );

    return setupIntentConfirmation.setupIntent;
  } catch (err) {
    errorHandler(err.message, options);
  }
}

async function _confirmSetupIntent(setupIntent, { elements, stripe }, options) {
  let confirmation = await stripe.confirmSetup({
    elements,
    clientSecret: setupIntent.clientSecret,
    confirmParams: {
      return_url: window.location.href,
      expand: ['payment_method'],
      payment_method_data: {
        billing_details: {
          name: options.cardholderName,
        },
      },
    },
    handleActions: false,
  });

  if (confirmation.error) {
    throw new Error(confirmation.error.message);
  }

  if (confirmation.setupIntent.status === 'requires_action') {
    throw new Error(
      'The card was declined because it requires authentication. Please try another card'
    );
  }

  return confirmation;
}

function _buildTestPaymentMethod(stripeCustomerId, paymentMethodTypes = []) {
  let isAch = paymentMethodTypes.includes('us_bank_account');
  let paymentMethod = {
    id: 'pm_123',
    customer: stripeCustomerId,
  };

  if (isAch) {
    paymentMethod.us_bank_account = {
      bank_name: 'STRIPE TEST BANK',
      last4: '6789',
      account_type: 'checking',
      status: 'active',
    };
  } else {
    paymentMethod.card = {
      brand: 'visa',
      last4: '4242',
      exp_month: 10,
      exp_year: 2023,
    };
    paymentMethod.billing_details = { name: 'Test payment method name' };
  }

  return { payment_method: paymentMethod };
}

function _getErrorTitle(paymentMethodType, intl) {
  if (!intl) return `Could not add a ${paymentMethodType}`;
  return paymentMethodType === 'ACH'
    ? intl.t('billing.errors.couldNotAddAch')
    : intl.t('billing.errors.couldNotAddCard');
}

function _errorBanner(message, options) {
  let paymentMethodType = options.paymentMethodType || 'card';
  let title = _getErrorTitle(paymentMethodType, options.intl);

  error({
    title,
    isTransient: true,
    content: createElement('p', { innerText: message }),
  });

  if (typeof options.errorTracking === 'function') {
    options.errorTracking(message, paymentMethodType);
  }
}
